// ---------------------------------------------------------------------------
// Tipos base das Vistas 2D (plantas e cortes) e álgebra do referencial.
//
// Planta e corte são o MESMO objecto: um plano no mundo, um referencial
// ortonormal (right, up) sobre esse plano e uma profundidade. O que muda
// é só a orientação do referencial.
//
// Frame do mundo (three): x = Este, y = Cima, z = -Norte.
//
// Convenção de sinal do viewDir (IMPORTANTE):
//   viewDir = up × right   (equivale a -(right × up))
// aponta PARA DENTRO da cena. Com a base canónica de planta
// (right = Este, up = Norte) dá (0,-1,0), ou seja, olhar para baixo,
// que é o que a planta exige. O corte fica em w = 0 e desenha-se o que
// está em 0 <= w <= depth.
//
// Tudo neste ficheiro é puro (sem three, sem DOM): números e arrays.
// ---------------------------------------------------------------------------

import type { Annotation2D } from './annotations'
import type { Dimension2D } from './dimensions'

export type Vec3 = [number, number, number]
export type Point2 = [number, number]

/** Segmento no espaço do mundo. */
export interface Segment3 {
  a: Vec3
  b: Vec3
}

/** Segmento já projectado no plano do desenho. */
export interface Segment2 {
  a: Point2
  b: Point2
}

/** Círculo completo, em coordenadas do desenho (metros). */
export interface Circle2D {
  cx: number
  cy: number
  r: number
}

/** Arco que substitui um troço de vértices consecutivos de uma
 *  polilinha. Os ângulos são radianos em [0, 2*pi), medidos do eixo +x
 *  no sentido directo. */
export interface Arc2D extends Circle2D {
  /** Índice, em `points`, do primeiro vértice do troço. */
  start: number
  /** Índice do último vértice. Num loop FECHADO pode valer `n` (o número
   *  de pontos), que quer dizer "de volta a points[0]". */
  end: number
  /** Ângulo do vértice inicial. */
  a0: number
  /** Ângulo do vértice final. */
  a1: number
  /** true = o arco vai de a0 para a1 no sentido directo (anti-horário). */
  ccw: boolean
}

/** Polilinha do desenho. `closed` implica o segmento de fecho entre o
 *  último e o primeiro ponto (o último ponto NÃO é repetido). */
export interface Polyline2D {
  points: Point2[]
  closed: boolean
  /** Elemento do modelo que originou esta linha (localId do Fragments).
   *  Serve para (a) agrupar os contornos fechados do MESMO elemento num
   *  só caminho de preenchimento, e assim os furos ficarem furos com a
   *  regra even-odd, e (b) seleccionar o elemento no 3D a partir de um
   *  clique no desenho. Os writers PDF e DXF ignoram-no. */
  localId?: number
  /** Arcos reconhecidos na tesselação (ver `arcFit.ts`). Campo
   *  PARALELO: os `points` continuam a ser a polilinha completa, por
   *  isso tudo o que não sabe de arcos (caixa envolvente, recorte,
   *  selecção pelo desenho, medição) continua a funcionar como antes.
   *  Ordenados por `start` e sem sobreposições. */
  arcs?: Arc2D[]
  /** O loop inteiro é um círculo (implica `closed`). Exclusivo com
   *  `arcs`. */
  circle?: Circle2D
}

/** Janela rectangular no plano do desenho (coordenadas right/up). */
export interface Crop2D {
  minX: number
  minY: number
  maxX: number
  maxY: number
}

/** Definição de uma vista 2D (planta ou corte). */
export interface View2DDef {
  id: string
  name: string
  kind: 'plan' | 'section'
  /** Ponto do plano de corte, em coordenadas de MUNDO. É também a
   *  origem do sistema 2D do desenho. */
  origin: Vec3
  /** Eixo X do desenho (unitário, coordenadas de mundo). */
  right: Vec3
  /** Eixo Y do desenho (unitário, coordenadas de mundo). */
  up: Vec3
  /** Profundidade de projecção, em metros, medida ao longo do viewDir. */
  depth: number
  crop?: Crop2D
  /** Escala de papel: 100 = 1:100. */
  scaleDenom?: number
}

/** Referencial já normalizado e ortogonalizado, com o viewDir derivado. */
export interface ViewBasis {
  origin: Vec3
  right: Vec3
  up: Vec3
  /** up × right: aponta para dentro da cena. */
  viewDir: Vec3
  depth: number
}

/** Plano infinito no mundo (usado pelo cutTriangles). */
export interface Plane3 {
  origin: Vec3
  /** Normal unitária. O lado positivo é o do sentido da normal. */
  normal: Vec3
}

// --- Álgebra vectorial mínima (evita depender do three aqui) ---------------

export function dot(a: Vec3, b: Vec3): number {
  return a[0] * b[0] + a[1] * b[1] + a[2] * b[2]
}

export function cross(a: Vec3, b: Vec3): Vec3 {
  return [
    a[1] * b[2] - a[2] * b[1],
    a[2] * b[0] - a[0] * b[2],
    a[0] * b[1] - a[1] * b[0],
  ]
}

export function sub(a: Vec3, b: Vec3): Vec3 {
  return [a[0] - b[0], a[1] - b[1], a[2] - b[2]]
}

export function length3(a: Vec3): number {
  return Math.hypot(a[0], a[1], a[2])
}

/** Normaliza; lança se o vector for (quase) nulo (erro de programação,
 *  não de dados: uma vista mal definida deve falhar cedo e alto). */
export function normalize(a: Vec3, what = 'vector'): Vec3 {
  const n = length3(a)
  if (!Number.isFinite(n) || n < 1e-12) {
    throw new Error(`Vistas 2D: ${what} degenerado (comprimento nulo)`)
  }
  return [a[0] / n, a[1] / n, a[2] / n]
}

/** Deriva o referencial da vista: normaliza o right, ortogonaliza o up
 *  contra ele (Gram-Schmidt) e calcula viewDir = up × right. */
export function viewBasis(view: View2DDef): ViewBasis {
  const right = normalize(view.right, 'eixo right')
  const upRaw = view.up
  const k = dot(upRaw, right)
  const upOrtho: Vec3 = [
    upRaw[0] - right[0] * k,
    upRaw[1] - right[1] * k,
    upRaw[2] - right[2] * k,
  ]
  const up = normalize(upOrtho, 'eixo up (paralelo ao right?)')
  const viewDir = normalize(cross(up, right), 'viewDir')
  const depth = Number.isFinite(view.depth) && view.depth > 0 ? view.depth : 0
  return { origin: view.origin, right, up, viewDir, depth }
}

/** Plano de corte da vista (w = 0), com a normal a apontar para dentro
 *  da cena. */
export function viewPlane(basis: ViewBasis): Plane3 {
  return { origin: basis.origin, normal: basis.viewDir }
}

// --- Fábricas de vistas ----------------------------------------------------

/** Profundidade por omissão de uma planta, em metros: chega para apanhar
 *  o pavimento e o mobiliário do piso sem varrer o piso de baixo. */
export const PLAN_DEFAULT_DEPTH = 4

/** Altura de corte convencional acima do piso, em metros (peitoril de
 *  janela: apanha portas e janelas correntes). */
export const PLAN_DEFAULT_CUT_OFFSET = 1.2

/** Constrói a planta de um piso. `elevation` é a cota do pavimento em
 *  metros (coordenada y do mundo); o plano de corte fica em
 *  elevation + cutOffset. As coordenadas do desenho ficam a ser
 *  directamente (Este, Norte) do mundo. */
export function planViewDef(opts: {
  id?: string
  name: string
  elevation: number
  cutOffset?: number
  depth?: number
  crop?: Crop2D
  scaleDenom?: number
}): View2DDef {
  const cutOffset = opts.cutOffset ?? PLAN_DEFAULT_CUT_OFFSET
  return {
    id: opts.id ?? `plan_${opts.name}`,
    name: opts.name,
    kind: 'plan',
    origin: [0, opts.elevation + cutOffset, 0],
    right: [1, 0, 0],
    up: [0, 0, -1],
    depth: opts.depth ?? PLAN_DEFAULT_DEPTH,
    crop: opts.crop,
    scaleDenom: opts.scaleDenom,
  }
}

/** A mesma planta com outra cota de corte, profundidade ou escala.
 *
 *  Serve o "Regenerar": muda só a altura do plano de corte (cota do piso
 *  + offset), a profundidade e a escala. Os eixos `right` e `up` ficam
 *  como estão, porque é neles que vive a rotação da vista (ver
 *  `rotateViewDef`); reconstruir a planta com `planViewDef` punha-a outra
 *  vez a direito e deixava a janela de recorte nos eixos errados. */
export function replanViewDef(
  view: View2DDef,
  opts: {
    elevation: number
    cutOffset: number
    depth: number
    scaleDenom?: number
  },
): View2DDef {
  return {
    ...view,
    origin: [view.origin[0], opts.elevation + opts.cutOffset, view.origin[2]],
    depth: opts.depth,
    scaleDenom: opts.scaleDenom ?? view.scaleDenom,
  }
}

/** Converte um azimute (graus a partir do Norte, sentido horário) na
 *  direcção de observação do mundo three: 0 = olhar para Norte,
 *  90 = olhar para Este. */
export function azimuthToViewDir(azimuthDeg: number): Vec3 {
  const a = (azimuthDeg * Math.PI) / 180
  return [Math.sin(a), 0, -Math.cos(a)]
}

/** Constrói um corte vertical. Recebe o ponto do plano e a direcção de
 *  observação (azimute ou vector); o up é sempre a vertical do mundo. */
export function sectionViewDef(opts: {
  id?: string
  name: string
  origin: Vec3
  azimuthDeg?: number
  viewDir?: Vec3
  depth: number
  crop?: Crop2D
  scaleDenom?: number
}): View2DDef {
  const raw = opts.viewDir ?? azimuthToViewDir(opts.azimuthDeg ?? 0)
  // Só interessa a componente horizontal: um corte tem o up vertical.
  const horiz = normalize([raw[0], 0, raw[2]], 'direcção do corte')
  const up: Vec3 = [0, 1, 0]
  const right = normalize(cross(horiz, up), 'eixo right do corte')
  return {
    id: opts.id ?? `section_${opts.name}`,
    name: opts.name,
    kind: 'section',
    origin: opts.origin,
    right,
    up,
    depth: opts.depth,
    crop: opts.crop,
    scaleDenom: opts.scaleDenom,
  }
}

// --- IR de desenho ---------------------------------------------------------

/** Camada do desenho: uma categoria IFC, com o traço de corte separado
 *  do traço de projecção (pesos e cores diferentes). */
export interface DrawingLayer {
  /** Ex.: 'IFCWALL'. Maiúsculas, seguro para nomes de layer de DXF. */
  name: string
  /** Linhas do plano de corte (peso forte). */
  cut: Polyline2D[]
  /** Linhas vistas para lá do plano (peso leve). */
  projection: Polyline2D[]
  colorHex?: string
  /** Cor do preenchimento (poché) das zonas cortadas. Ausente = o writer
   *  deriva-a da `colorHex` (ver `SVG_FILL_FADE`). */
  fillHex?: string
}

/** Contagens da geração, para diagnóstico na UI (fase 2). */
export interface View2DStats {
  /** Elementos candidatos depois do pré-filtro por caixa. */
  candidates: number
  /** Elementos que produziram pelo menos um segmento de corte. */
  cutItems: number
  /** Elementos que produziram projecção. */
  projectedItems: number
  /** Partes de geometria ilegíveis saltadas. */
  skipped: number
  /** true quando o limite de triângulos cortou a recolha. */
  truncated: boolean
  triangles: number
  /** Custo da remoção de linhas ocultas, quando ligada. Ausente = a
   *  vista foi gerada sem HLR. */
  hiddenLines?: {
    /** Células do mapa de profundidade. */
    cells: number
    /** Lado da célula, em metros de modelo. */
    cellM: number
    /** Tempo de construção do mapa, em ms. */
    ms: number
  }
}

/** Representação intermédia do desenho: independente do formato final.
 *  Coordenadas em METROS no plano do desenho (x = right, y = up). */
export interface Drawing2D {
  name: string
  scaleDenom: number
  unit: 'm'
  bbox: { minX: number; minY: number; maxX: number; maxY: number }
  layers: DrawingLayer[]
  /** Etiquetas de texto do utilizador (quarta leva). Não nascem do
   *  modelo: o painel injecta-as antes de escrever/desenhar. Ausente nos
   *  desenhos que ninguém anotou. Ver `annotations.ts`. */
  annotations?: Annotation2D[]
  /** Cotas: medições que o utilizador deixou desenhadas (quinta leva).
   *  Como as anotações, não nascem do modelo: o painel injecta-as antes
   *  de escrever. Ver `dimensions.ts`. */
  dimensions?: Dimension2D[]
  /** Preenchido pelo generateView2D; os writers ignoram-no. */
  stats?: View2DStats
}

/** Escala por omissão quando a vista não declara nenhuma. */
export const DEFAULT_SCALE_DENOM = 100

/** Nome da camada usada quando não há categoria conhecida. */
export const DEFAULT_LAYER = 'GERAL'

// --- Rodar a vista ----------------------------------------------------
//
// Rodar uma vista NÃO é guardar um ângulo: é rodar os eixos `right` e
// `up` em torno da direcção de observação. Tudo o que vem a seguir (o
// corte, a projecção, as linhas escondidas, as cotas, o PDF e o DXF)
// deriva desses dois eixos, portanto acompanha sozinho.
//
// O que NÃO muda: a `origin`, a `depth`, e sobretudo a geometria do
// modelo. Uma vista rodada é uma maneira de olhar, não uma alteração ao
// edifício. É o que permite ter isto sem tocar na georreferenciação nem
// desalinhar uma federação.

/** Roda um vector em torno de um eixo unitário (fórmula de Rodrigues). */
export function rotateAround(v: Vec3, axis: Vec3, rad: number): Vec3 {
  const c = Math.cos(rad)
  const s = Math.sin(rad)
  const k = dot(axis, v) * (1 - c)
  const cr = cross(axis, v)
  return [
    v[0] * c + cr[0] * s + axis[0] * k,
    v[1] * c + cr[1] * s + axis[1] * k,
    v[2] * c + cr[2] * s + axis[2] * k,
  ]
}

/** A mesma vista, rodada `deg` graus no plano do desenho.
 *
 *  Positivo roda o CONTEÚDO no sentido anti-horário, que é o que se
 *  espera ao dizer "roda a planta 30 graus". A folha não se mexe. */
export function rotateViewDef(view: View2DDef, deg: number): View2DDef {
  const basis = viewBasis(view)
  const rad = (-deg * Math.PI) / 180
  return {
    ...view,
    right: rotateAround(basis.right, basis.viewDir, rad),
    up: rotateAround(basis.up, basis.viewDir, rad),
    // A janela de recorte foi desenhada nos eixos antigos e deixaria de
    // querer dizer o mesmo: quem roda a vista redefine-a.
    crop: undefined,
  }
}

/** Ângulo, em graus, que põe o segmento a→b horizontal no desenho.
 *
 *  Os pontos vêm em coordenadas do DESENHO (as mesmas que o utilizador
 *  pica). Serve tanto para alinhar por dois pontos como para alinhar a
 *  um elemento: basta dar-lhe dois pontos da sua direcção. */
export function alignAngleDeg(a: Point2, b: Point2): number {
  const dx = b[0] - a[0]
  const dy = b[1] - a[1]
  if (dx === 0 && dy === 0) return 0
  return (Math.atan2(dy, dx) * 180) / Math.PI
}
